import { useState } from 'react';
import { addressKeys, formatAddress, isStructuredAddress, type StructuredAddress } from '../shared/address';
import type { JsonValue } from '../shared/types';

const labels = {
	line1: 'Address Line 1',
	line2: 'Address Line 2 (apartment, suite, etc.; optional)',
	city: 'City / Town / Locality',
	region: 'State / Province / Region (if applicable)',
	postalCode: 'ZIP / Postal Code (if applicable)',
	country: 'Country',
};

export function AddressInput({
	id,
	value,
	onChange,
	disabled,
}: {
	id: string;
	value: JsonValue | undefined;
	onChange: (value: JsonValue) => void;
	disabled: boolean;
}) {
	const [converting, setConverting] = useState(false);
	const [draft, setDraft] = useState<StructuredAddress>({});
	const legacy = typeof value === 'string' && value.length > 0;
	const address = converting ? draft : isStructuredAddress(value) ? value : {};
	function update(key: (typeof addressKeys)[number], text: string) {
		const next = { ...address, [key]: text };
		if (converting) setDraft(next);
		else onChange(next);
	}
	return (
		<fieldset className="address-input" id={id} disabled={disabled}>
			<legend>Address details</legend>
			{legacy && (
				<>
					<p className="help">Previously saved address. It is preserved as entered; we have not guessed its separate parts.</p>
					<textarea
						aria-label="Previously saved address"
						value={value}
						rows={3}
						readOnly={converting}
						onChange={(event) => onChange(event.target.value)}
					/>
					{!converting && (
						<button type="button" className="secondary" onClick={() => setConverting(true)}>
							Enter separate address fields
						</button>
					)}
				</>
			)}
			{(!legacy || converting) && (
				<>
					<div className="address-fields">
						{addressKeys.map((key) => (
							<label key={key} htmlFor={`${id}-${key}`}>
								{labels[key]}
								<input
									id={`${id}-${key}`}
									type="text"
									value={address[key] ?? ''}
									maxLength={500}
									onChange={(event) => update(key, event.target.value)}
								/>
							</label>
						))}
					</div>
					<p className="help">
						Use the address as written locally. Letters and international postal codes are accepted. Leave fields that do not apply blank.
					</p>
					{formatAddress(address) && (
						<div className="address-preview" aria-label="Address preview">
							{formatAddress(address)}
						</div>
					)}
					{converting && (
						<div className="form-navigation">
							<button
								type="button"
								disabled={!formatAddress(draft)}
								onClick={() => {
									onChange(draft);
									setConverting(false);
								}}
							>
								Use this address
							</button>
							<button
								type="button"
								className="secondary"
								onClick={() => {
									setConverting(false);
									setDraft({});
								}}
							>
								Keep previous address
							</button>
						</div>
					)}
				</>
			)}
		</fieldset>
	);
}
